import React from 'react';
import {
  EntityTimeline,
  type TimelineEvent,
  type TimelineSortOrder,
} from '@locus-taxy/encore-ui-toolkit';
import {
  useEntitySearch,
  type Condition,
  type ConditionGroup,
  type EntitySearchRequest,
} from '../../api/endpoints/useEntitySearch';
import type { WorkflowTimelineEntryData } from '../../types/workflowTimelineEntry';
import TimelineEmptyState from './TimelineEmptyState';

interface WorkflowTimelineContainerProps {
  clientId: string;
  rootInstanceKey: string;
  title?: string;
  entityName?: string;
}

// Minimal filter model types aligned with toolkit's FilterPopover
type FilterCondition = {
  field?: string;
  operator: string;
  value?: unknown;
  lowerBound?: unknown;
  upperBound?: unknown;
  pattern?: string;
  caseSensitive?: boolean;
};

type FilterModel = {
  operator: 'AND' | 'OR' | 'NOT';
  conditions: FilterCondition[];
};

export default function WorkflowTimelineContainer({
  clientId,
  rootInstanceKey,
  title,
  entityName,
}: WorkflowTimelineContainerProps) {
  const [appliedFilters, setAppliedFilters] = React.useState<
    FilterModel | undefined
  >(undefined);
  const [timelineSort, setTimelineSort] =
    React.useState<TimelineSortOrder>('newest');
  const [searchValue, setSearchValue] = React.useState<string>('');

  // Base conditions that are always applied: filter by rootInstanceKey and entryType='ACTION'
  // Memoized to avoid recreating on every render, depends on rootInstanceKey
  const baseConditions = React.useMemo<Condition[]>(
    () => [
      {
        field: 'entityData.rootMeta.rootInstanceKey',
        operator: 'EQ',
        value: rootInstanceKey,
      },
      {
        field: 'entityData.entryType',
        operator: 'EQ',
        value: 'ACTION',
      },
    ],
    [rootInstanceKey]
  );

  const [filterRequest, setFilterRequest] = React.useState<EntitySearchRequest>(
    () => ({
      request: {
        condition: {
          operator: 'AND',
          conditions: baseConditions,
        },
        sort: [{ field: 'entityData.triggeredOn', direction: 'DESC' }],
        pagination: { page: 1, size: 50 },
      },
    })
  );

  const timelineEntityName = entityName
    ? `workflowTimelineEntry_${entityName}`
    : 'workflowTimelineEntry';

  const { data, isLoading, refetch } =
    useEntitySearch<WorkflowTimelineEntryData>(
      clientId,
      timelineEntityName,
      filterRequest,
      { enabled: Boolean(clientId && rootInstanceKey && entityName) }
    );

  // Single source of truth: compose filterRequest from baseConditions and appliedFilters
  // This effect handles all condition composition and flattening logic
  React.useEffect(() => {
    setFilterRequest(prev => {
      // Extract user conditions from appliedFilters, filtering out:
      // 1. Conditions without a field
      // 2. Conditions that conflict with baseConditions (entryType, rootInstanceKey)
      // 3. Nested ConditionGroups (only handle flat Conditions)
      const userConditions: Condition[] = appliedFilters
        ? (appliedFilters.conditions || [])
            .filter(
              (condition): condition is FilterCondition =>
                'field' in condition &&
                condition.field !== undefined &&
                condition.field !== 'entityData.entryType' &&
                condition.field !== 'entityData.rootMeta.rootInstanceKey'
            )
            .map(
              condition =>
                ({
                  field: condition.field!,
                  operator: condition.operator,
                  value: condition.value,
                  pattern: condition.pattern,
                  lowerBound: condition.lowerBound,
                  upperBound: condition.upperBound,
                  caseSensitive: false,
                }) as Condition
            )
        : [];

      // Flatten conditions: combine baseConditions with user conditions
      const combinedCondition: ConditionGroup = {
        operator: 'AND',
        conditions: [...baseConditions, ...userConditions],
      };

      return {
        request: {
          ...prev.request,
          condition: combinedCondition,
        },
      };
    });
  }, [baseConditions, appliedFilters]);

  React.useEffect(() => {
    // Force refetch whenever the request object changes
    refetch();
  }, [filterRequest, refetch]);

  const events = React.useMemo<TimelineEvent[]>(() => {
    const entries = data?.data?.content ?? [];
    return entries.map(e => {
      const d = e.entityData;
      // For actions, use actionMeta.actionName; otherwise use stepName or jobType
      const title =
        d.entryType === 'ACTION'
          ? d.actionMeta?.actionName || d.entryMeta?.stepName || 'Event'
          : d.entryMeta?.stepName || d.entryMeta?.jobType || 'Event';
      const ev: TimelineEvent = {
        id: String(d.id),
        occurredAt: d.triggeredOn,
        title,
        subtitle: d.actionMeta?.actorId
          ? `by ${d.actionMeta.actorId}`
          : undefined,
        metadata: d as unknown as Record<string, unknown>,
      };
      return ev;
    });
  }, [data?.data?.content]);

  const getEventDetails = React.useCallback((event: TimelineEvent) => {
    const meta = (event.metadata || {}) as unknown as WorkflowTimelineEntryData;
    return {
      inputData: meta.inputData || {},
      outputData: meta.outputData || {},
    };
  }, []);

  const handleApplyFilter = React.useCallback((filterInfo: ConditionGroup) => {
    // Only update appliedFilters; the effect will handle composing filterRequest
    setAppliedFilters(filterInfo);
  }, []);

  const handleResetFilters = React.useCallback(() => {
    // Only update appliedFilters; the effect will handle composing filterRequest
    setAppliedFilters(undefined);
  }, []);

  const handleSortChange = React.useCallback((sort: TimelineSortOrder) => {
    setTimelineSort(sort);
    setFilterRequest(prev => ({
      request: {
        ...prev.request,
        sort: [
          {
            field: 'entityData.triggeredOn',
            direction: sort === 'newest' ? 'DESC' : 'ASC',
          },
        ],
        pagination: { ...prev.request.pagination, page: 1 },
      },
    }));
  }, []);

  const handleSearchChange = React.useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const filterColumns = React.useMemo(
    () => [
      {
        field: 'entityData.actionMeta.actorId',
        headerText: 'Actor',
        isFilterable: true,
        filterType: 'search',
        visible: true,
      },
      {
        field: 'entityData.actionMeta.actionName',
        headerText: 'Action',
        isFilterable: true,
        filterType: 'search',
        visible: true,
      },
      {
        field: 'entityData.triggeredOn',
        headerText: 'Occurred At',
        isFilterable: true,
        filterType: 'dateTimeRange',
        visible: true,
      },
    ],
    []
  );

  return (
    <EntityTimeline
      title={title}
      events={events}
      isLoading={isLoading}
      sort={timelineSort}
      onSortChange={handleSortChange}
      searchValue={searchValue}
      onSearchChange={handleSearchChange}
      getEventDetails={getEventDetails}
      EmptyState={TimelineEmptyState}
      // @ts-expect-error: Toolkit's LogicalCondition type isn't exported; we pass a compatible shape
      filterInfo={appliedFilters as unknown as LogicalCondition}
      // @ts-expect-error: Toolkit's ColumnType type isn't exported; we pass a compatible shape
      filterColumns={filterColumns}
      onApplyFilter={handleApplyFilter as (filterInfo: FilterCondition) => void}
      onResetFilters={handleResetFilters}
      routeContext={{
        entity: entityName,
        page: 'entity-details-timeline',
      }}
    />
  );
}
